// Pure card-selection logic for the map's bottom carousel (#250).
//
// Google Maps is not testable in jsdom, so the decisions that used to live inline in the
// `updateCards` closure in App.js live here instead: which locations become cards, whether the
// selected marker is pinned into the list, and whether the previous list survives an empty
// viewport. App.js supplies the viewport/bounds facts; this module owns the rules.

const DEFAULT_CARD_LIMIT = 8;

function sameCatalogRef(a, b) {
  return Boolean(a) && Boolean(b) && a.type === b.type && String(a.id) === String(b.id);
}

export function containsCatalogRef(locations, target) {
  if (!target || !Array.isArray(locations)) return false;
  return locations.some(candidate => sameCatalogRef(candidate, target));
}

/**
 * Compute the next carousel card list.
 *
 * @param {object[]} locationsInView  in-viewport locations, already in display order.
 * @param {object[]} selectionPool    every location passing the active filters (viewport
 *                                    irrelevant). The selected marker is pinned only if it is
 *                                    still in here. Defaults to `locationsInView`.
 * @param {object|null} selected      the currently selected marker, if any.
 * @param {object[]} previousCards    the list currently on screen.
 * @param {boolean} filtersActive     true when a category or village filter is set.
 * @param {number} limit              max cards (default 8).
 */
export function computeVisibleMapCards({
  locationsInView = [],
  selectionPool = null,
  selected = null,
  previousCards = [],
  filtersActive = false,
  limit = DEFAULT_CARD_LIMIT,
} = {}) {
  const cards = locationsInView.slice(0, limit);

  // The selected marker is pinned into the carousel even when the user panned it out of view —
  // but only while it still passes the active filters. Before #250 it was pinned unconditionally,
  // so a marker selected before filtering stayed on screen after it stopped matching.
  const pool = Array.isArray(selectionPool) ? selectionPool : locationsInView;
  const pinSelected = Boolean(selected)
    && containsCatalogRef(pool, selected)
    && !containsCatalogRef(cards, selected);
  const next = pinSelected ? [selected, ...cards.slice(0, Math.max(0, limit - 1))] : cards;

  // Keeping the previous cards over an empty viewport stops the carousel flickering while the
  // user pans across a gap. With filters active, empty is the honest answer — nothing here
  // matches — and preserving would put non-matching places back on screen (#250).
  if (next.length === 0 && !filtersActive) return previousCards;
  return next;
}
